import { type GradeCategory, type Lesson } from "@core/grades/types";
import { URLS } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { useIsRTL } from "@framework/shared/hooks";
import { trans } from "@mongez/localization";
import { ArrowRight, FolderOpen } from "lucide-react";
import { LessonDiscoveryCard } from "./LessonDiscoveryCard";

type CategoryLessonShelfProps = {
  category?: GradeCategory;
  categoryName: string;
  lessons: Lesson[];
  shelfIndex: number;
};

export function CategoryLessonShelf({
  category,
  categoryName,
  lessons,
  shelfIndex,
}: CategoryLessonShelfProps) {
  const isRTL = useIsRTL();

  return (
    <section
      className="scroll-mt-24"
      id={`category-${category?.id || shelfIndex}`}
    >
      <div className="mb-4 flex items-end justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="dark:from-primary-950 grid size-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-primary-100 to-violet-100 text-primary-700 dark:to-violet-950 dark:text-primary-200">
            <FolderOpen className="size-5" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-xl font-black text-slate-950 dark:text-white sm:text-2xl">
              {categoryName}
            </h2>
            <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
              {trans("grades.totalLessons", { count: lessons.length })}
            </p>
          </div>
        </div>

        {category ? (
          <BaseLink
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:border-primary-300 hover:text-primary-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-primary-700 dark:hover:text-primary-300"
            to={URLS.grades.viewCategory(category)}
          >
            {trans("viewMore")}
            <ArrowRight className={`size-3.5 ${isRTL ? "rotate-180" : ""}`} />
          </BaseLink>
        ) : null}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {lessons.map((lesson, lessonIndex) => (
          <LessonDiscoveryCard
            accentIndex={shelfIndex + lessonIndex}
            key={lesson.id}
            lesson={lesson}
          />
        ))}
      </div>
    </section>
  );
}
